import React from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import ErrorBoundary from 'components/ErrorBoundary';
import RecordView from 'components/RecordView';
import Spacing from 'components/Spacing';
import FieldRow from 'components/FormElements/FieldRow';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  ConsiderationType,
  ConsiderationTypeHoldingClassTypes,
  PartialRealisationAllocationLogic,
} from 'further-types/lp/realisation';
import TransactionAllocator from 'components/TransactionAllocator';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import PageLoader from 'components/PageLoader';
import CashConsiderationsTable from '../CreateRealisation/Summary/CashConsiderationsTable';
import api from 'lib/trpcClient.ts';
import getRealisationType from '../CreateRealisation/Summary/transactions/helpers/getRealisationType.ts';
import ConsiderationOverview from '../CreateRealisation/Summary/ConsiderationOverview.tsx/index.tsx';

type HoldingClassRealisationAllocation = {
  holdingClassId: string;
  name: string;
  partialRealisationAllocationLogic: PartialRealisationAllocationLogic;
  unitsSold: number;
  unitsAllocationPerInvestment: Array<{
    investmentId: string;
    investmentCurrency: CurrencyCode;
    allocatedUnits: number;
    receivedConsiderationTotalTcy: number;
    escrowConsiderationTotalTcy: number;
    earnoutConsiderationTotalTcy: number;
    withholdingTaxPayableTcy: number;
    receivedConsiderationTotalLcy: number;
    escrowConsiderationTotalLcy: number;
    earnoutConsiderationTotalLcy: number;
    withholdingTaxPayableLcy: number;
  }>;
};

type ConsiderationEntry = {
  perHoldingClassConsiderationAmounts: Array<{
    holdingClassId: string;
    receivedConsiderationPerUnitTcy: number;
    receivedConsiderationTotalTcy: number;
    escrowConsiderationTotalTcy: number;
    earnoutConsiderationTotalTcy: number;
    withholdingTaxPayableTcy: number;
  }>;
};

const formatHoldingClassRealisation = (
  allocation: HoldingClassRealisationAllocation,
  cashConsiderationEntry?: ConsiderationEntry,
) => {
  if (!cashConsiderationEntry) {
    throw new Error('Cash consideration entry not found');
  }

  const considerationAmounts =
    cashConsiderationEntry?.perHoldingClassConsiderationAmounts.find(
      (amount) => amount.holdingClassId === allocation.holdingClassId,
    );

  return {
    holdingClassId: allocation.holdingClassId,
    name: allocation.name,
    partialRealisationAllocationLogic:
      allocation.partialRealisationAllocationLogic,
    unitsSold: allocation.unitsSold,
    cashConsiderationAmounts: {
      receivedConsiderationPerUnit:
        considerationAmounts?.receivedConsiderationPerUnitTcy ?? 0,
      receivedConsiderationTotal:
        considerationAmounts?.receivedConsiderationTotalTcy ?? 0,
      escrowConsiderationTotal:
        considerationAmounts?.escrowConsiderationTotalTcy ?? 0,
      earnoutConsiderationTotal:
        considerationAmounts?.earnoutConsiderationTotalTcy ?? 0,
      withholdingTaxPayable:
        considerationAmounts?.withholdingTaxPayableTcy ?? 0,
    },
  };
};

const breadcrumbs = [
  { label: 'Dashboard' },
  { label: 'Realisations', link: '/realisation/list' },
  { label: 'View Realisation', link: '/', isActive: true },
];

const ViewRealisation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const [realisation] = api.lpRealisation.get.useSuspenseQuery({
    realisationId: id ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  if (!vehicle) {
    return <PageLoader />;
  }

  const cashConsiderationEntry = realisation.considerationEntries.find(
    (entry) =>
      entry.considerationType === ConsiderationType.CashAndWithholdingTax,
  );

  const considerationCurrency =
    cashConsiderationEntry?.currencyCode ?? vehicle?.baseCurrency;

  const holdingClassCurrencies =
    realisation.holdingClassRealisationAllocations.flatMap((holdingClass) =>
      holdingClass.unitsAllocationPerInvestment?.map(
        (investment) => investment.investmentCurrency,
      ),
    );

  const holdingClassCurrency =
    holdingClassCurrencies[0] ?? vehicle?.baseCurrency;

  const realisationType = getRealisationType({
    holdingClassInvestment: {
      currency: holdingClassCurrency ?? considerationCurrency,
    },
    vehicleCurrencyCode: vehicle?.baseCurrency,
    realisationCurrencyCode: realisation.currencyCode,
  });

  const currencyForTcyDisplay =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
      ? holdingClassCurrency ?? considerationCurrency
      : considerationCurrency;

  const forceShowTcy =
    currencyForTcyDisplay !== realisation.currencyCode &&
    realisation.currencyCode !== vehicle.baseCurrency;

  const formattedHoldingClassRealisations =
    realisation.holdingClassRealisationAllocations.map((allocation) =>
      formatHoldingClassRealisation(allocation, cashConsiderationEntry),
    );

  const investorAllocationsTotals =
    cashConsiderationEntry?.investorAllocations?.map((allocation) => ({
      commitmentId: allocation.commitmentId,
      investorName: allocation.investorLabel,
      receivedConsideration: allocation.receivedConsiderationTcy,
      escrowConsideration: allocation.escrowConsiderationTcy,
      earnoutConsideration: allocation.earnoutConsiderationTcy,
      withholdingTaxPayable: allocation.withholdingTaxPayableTcy,
      averageAllocationPercentage: allocation.allocationPercentage,
      total:
        allocation.receivedConsiderationTcy +
        allocation.escrowConsiderationTcy +
        allocation.earnoutConsiderationTcy,
    }));

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  return (
    <PageContainer
      heading={`Realisation: ${realisation.assetName}`}
      pageTitle="Realisations: View Realisations"
      breadcrumbs={breadcrumbs}
    >
      <PageContent withSpacing>
        <ErrorBoundary>
          <PageSection title="Realisation Details">
            <Spacing size="md">
              <Grid container>
                <Grid size={{ xs: 12, md: 8 }}>
                  <RecordView>
                    <FieldRow title="Vehicle">
                      <Typography variant="body2">
                        {realisation.vehicleName}
                      </Typography>
                    </FieldRow>
                    <FieldRow title="Realised asset">
                      <Typography variant="body2">
                        {realisation.assetName}
                      </Typography>
                    </FieldRow>
                    <FieldRow title="Realisation date">
                      <Typography variant="body2">
                        {realisation.realisationDate.toLabel()}
                      </Typography>
                    </FieldRow>
                    <FieldRow title="Classes realised">
                      <Typography variant="body2">
                        {realisation.holdingClassRealisationAllocations
                          .map((allocation) => allocation.name)
                          .join(', ')}
                      </Typography>
                    </FieldRow>
                    <FieldRow title="Total units realised">
                      <Typography variant="body2">
                        {numberToDisplayString(
                          realisation.numberOfUnitsRealised,
                        )}
                      </Typography>
                    </FieldRow>
                    <FieldRow title="Total realisation value">
                      <Typography variant="body2">
                        {numberToCurrencyString(
                          realisation.totalRealisationAmountLcy,
                          {
                            currency: realisation.currencyCode,
                          },
                        )}
                      </Typography>
                    </FieldRow>

                    <CashConsiderationsTable
                      currency={realisation.currencyCode}
                      holdingClassRealisations={
                        formattedHoldingClassRealisations
                      }
                    />
                  </RecordView>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <ConsiderationOverview
                  currency={realisation.currencyCode}
                  holdingClassRealisations={formattedHoldingClassRealisations}
                  investorAllocationsTotals={investorAllocationsTotals ?? []}
                />
              </Grid>

              <PageSection title="Account transactions">
                {vehicle && (
                  <TransactionAllocator
                    transactionTypeOptions={transactionTypeOptions}
                    transactions={realisation.transactions ?? []}
                    transactionCurrency={currencyForTcyDisplay}
                    fxRate={realisation.currencyRate ?? null}
                    vehicle={vehicle}
                    forceShowTcy={forceShowTcy}
                    readOnly={true}
                    showCurrencyRate={true}
                  />
                )}
              </PageSection>
            </Spacing>
          </PageSection>
        </ErrorBoundary>
      </PageContent>
    </PageContainer>
  );
};

export default ViewRealisation;
